import type { RxJsonSchema } from 'rxdb'

/**
 * CustomFieldValue Document
 *
 * Stores the actual value of a custom field for a specific task (EAV pattern).
 * Only one of stringValue/numberValue/dateValue is populated based on the
 * definition's fieldType. For list-type fields, stringValue stores the option id.
 *
 * Separate collection (not JSON on task) so custom field changes don't trigger
 * full task document replication. Matches the TaskContent split pattern.
 */
export interface CustomFieldValueDocument {
  id: string
  envId: string
  taskId: string
  customFieldDefinitionId: string

  stringValue: string | null
  numberValue: number | null
  dateValue: number | null

  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

export const customFieldValueSchema: RxJsonSchema<CustomFieldValueDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    taskId: {
      type: 'string',
      maxLength: 36,
    },
    customFieldDefinitionId: {
      type: 'string',
      maxLength: 36,
    },
    stringValue: {
      type: ['string', 'null'],
      maxLength: 2000,
    },
    numberValue: {
      type: ['number', 'null'],
    },
    dateValue: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'taskId',
    'customFieldDefinitionId',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    ['taskId', 'isDeleted'],
    ['taskId', 'customFieldDefinitionId'],
    'updatedOn',
  ],
}

export const customFieldValueMigrationStrategies = {}
